<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-card__accent"></div>

      <div class="login-brand">
        <div class="login-brand__mark">点</div>
        <div class="login-brand__text">
          <h1 class="login-brand__name">点墨资产管理系统</h1>
          <p class="login-brand__slogan">联营投资 · 全周期管理</p>
        </div>
      </div>

      <el-form
        ref="loginFormRef"
        :model="form"
        :rules="rules"
        label-position="top"
        class="login-form"
        @submit.prevent
      >
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="form.username"
            :prefix-icon="User"
            placeholder="请输入用户名"
            size="large"
            autocomplete="username"
          />
        </el-form-item>

        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            :prefix-icon="Lock"
            type="password"
            show-password
            placeholder="请输入密码"
            size="large"
            autocomplete="current-password"
            @keyup.enter="handleLogin"
          />
        </el-form-item>

        <div class="login-extra">
          <el-checkbox v-model="remember">记住用户名</el-checkbox>
          <el-link type="primary" :underline="false" @click="handleForgot">忘记密码？</el-link>
        </div>

        <div v-if="errorMsg" class="login-error">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ errorMsg }}</span>
        </div>

        <el-button
          type="primary"
          size="large"
          class="login-submit"
          :loading="loading"
          @click="handleLogin"
        >
          {{ loading ? '登录中…' : '登 录' }}
        </el-button>
      </el-form>

      <p class="login-note">账号由管理员统一分配，如需开通请联系系统管理员</p>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock, WarningFilled } from '@element-plus/icons-vue'
import authService from '../../services/authService'
import { setToken, setTenantId, setUserInfo } from '@/utils/auth'
import store from '@/store'

const REMEMBER_KEY = 'rbf_login_username'

export default {
  name: 'LoginView',
  components: { User, Lock, WarningFilled },
  setup() {
    const router = useRouter()
    const loginFormRef = ref(null)
    const loading = ref(false)
    const errorMsg = ref('')
    const remember = ref(false)

    const form = reactive({
      username: '',
      password: '',
      tenantId: '1'
    })

    const rules = {
      username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
      password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
    }

    // 回填上次记住的用户名
    onMounted(() => {
      const saved = localStorage.getItem(REMEMBER_KEY)
      if (saved) {
        form.username = saved
        remember.value = true
      }
    })

    const saveRemember = () => {
      if (remember.value && form.username) {
        localStorage.setItem(REMEMBER_KEY, form.username)
      } else {
        localStorage.removeItem(REMEMBER_KEY)
      }
    }

    const handleLogin = async () => {
      if (!loginFormRef.value) return
      try {
        await loginFormRef.value.validate()
      } catch {
        return
      }

      loading.value = true
      errorMsg.value = ''
      try {
        const response = await authService.login(form)
        setToken(response.token)
        setUserInfo(response.sysUser)
        setTenantId(response.sysUser.tenantId)

        store.commit('SET_TOKEN', response.token)
        store.commit('SET_USER_INFO', response.sysUser)
        store.commit('SET_TENANT_ID', response.sysUser.tenantId)

        saveRemember()
        ElMessage.success('登录成功')
        router.push('/dashboard')
      } catch (error) {
        errorMsg.value = error?.message || '用户名或密码错误，请重试'
      } finally {
        loading.value = false
      }
    }

    const handleForgot = () => {
      ElMessage.info('请联系系统管理员重置密码')
    }

    return {
      loginFormRef,
      form,
      rules,
      loading,
      errorMsg,
      remember,
      handleLogin,
      handleForgot
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--bg-page);
}

.login-card {
  position: relative;
  width: 400px;
  max-width: 100%;
  padding: 36px 32px 24px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-modal);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

/* 顶部品牌色条 */
.login-card__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--brand-500);
}

.login-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.login-brand__mark {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 10px;
  background: var(--brand-500);
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-brand__name {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: var(--text-primary);
  letter-spacing: .3px;
}

.login-brand__slogan {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

.login-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

.login-form :deep(.el-form-item__label) {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  padding-bottom: 5px;
}

.login-extra {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: -4px 0 20px;
  font-size: 12px;
}

.login-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 11px;
  margin-bottom: 16px;
  border-radius: var(--radius-control);
  background: var(--danger-bg);
  color: var(--danger-text);
  font-size: 12px;
  line-height: 1.5;
}

.login-submit {
  width: 100%;
  font-size: 14px;
  letter-spacing: 2px;
}

.login-note {
  margin: 20px 0 0;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 20px 20px;
    box-shadow: none;
    border-color: transparent;
  }
}
</style>
